import React, { useState, useEffect } from 'react'
import { Alert, Box, TextField, Button, TableContainer, Table, TableHead, TableCell, TableRow, Paper, TableBody, Select, MenuItem, CircularProgress, FormControl } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { downloadDocument, getBiocharLabSamples, postBiocharLabSamples } from '../../../../../actions/tigercat-burns/tigercat-actions';
import { v4 as uuidv4 } from 'uuid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

const BiocharLabSamples = ({ projectId, project }) => {
    const [biocharLabSamplesState, setBiocharLabSamplesState] = useState([])
    const dispatch = useDispatch()
    const labReportSamples = useSelector(state => state.tigercatBurnsReducer.biocharLabSamples.data)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    useEffect(() => {
        dispatch(getBiocharLabSamples(projectId));
    }, [dispatch, projectId]);

    useEffect(() => {
        const formDataArray = labReportSamples?.map((item) => {
            const formData = new FormData();
            formData.append('id', item.id);
            formData.append('sampleNumber', item.sampleNumber);
            formData.append('sampleDate', item.sampleDate);
            formData.append('sampleType', item.sampleType);
            formData.append('hCorg', item.hCorg);
            formData.append('organicCarbonContent', item.organicCarbonContent);
            formData.append('labReport', item.labReportUrl);
            formData.append('comments', item.comments);
            formData.append('disabled', true)
            return formData;
        });
        setBiocharLabSamplesState(formDataArray);
    }, [labReportSamples]);

    const handleAddItem = () => {
        setBiocharLabSamplesState((prevState) => {
            const state = [...prevState]
            const formData = new FormData();
            formData.append('id', null);
            formData.append('rowId', uuidv4())
            const maxSampleNumber = biocharLabSamplesState.reduce((max, item) => {
                const sampleNumber = parseInt(item.get('sampleNumber'), 10);
                return !isNaN(sampleNumber) && sampleNumber > max ? sampleNumber : max;
            }, 0);
            formData.append('sampleNumber', maxSampleNumber + 1);
            formData.append('sampleDate', '');
            formData.append('sampleType', '');
            formData.append('hCorg', '');
            formData.append('organicCarbonContent', '');
            formData.append('labReport', null);
            formData.append('comments', '');
            state.push(formData)
            return state
        })
    }

    const handleDateSelection = (date, dataId) => {
        setBiocharLabSamplesState((prevState) => {
            return prevState.map((item) => {
                if (item.get('id') === dataId) {
                    const formattedDate = moment(date).format('YYYY-MM-DD');
                    item.set('sampleDate', formattedDate);
                }
                return item;
            });
        });
    };

    const handleInputChange = (e, dataId, field) => {
        setBiocharLabSamplesState((prevState) => {
            return prevState.map((item) => {
                if (item.get('rowId') === dataId) {
                    item.set(field, e.target.value);
                }
                return item;
            });
        });
    }

    const handleSourceDoc = (e, dataId) => {
        const file = e.target.files[0];
        setBiocharLabSamplesState((prevState) => {
            return prevState.map((item) => {
                if (item.get('rowId') === dataId) {
                    item.set('labReport', file);
                }
                return item;
            });
        });
    }

    const sampleTypes = [
        { id: 0, name: 'IPS' },
        { id: 1, name: 'RS' }
    ]

    const handleSubmitData = () => {
        setLoading(true)
        let hasError = false
        biocharLabSamplesState.forEach((i) => {
            if (i.get('sampleNumber') === '' || i.get('sampleNumber') === '' || i.get('sampleType') === '' || i.get('hCorg') === '' || i.get('organicCarbonContent') === '' || i.get('labReport') === null || i.get('comments') === '') {
                setError(true)
                hasError = true
                return
            }
        })
        if (!hasError) {
            // console.log(biocharLabSamplesState)
            dispatch(postBiocharLabSamples(projectId, biocharLabSamplesState)).then(() => {
                dispatch(getBiocharLabSamples(projectId))
                setLoading(false)
            })
        } else {
            setLoading(false)
        }
    }

    const handleDownload = async () => {
        try {
            dispatch(downloadDocument(projectId, 'biochar_lab_samples')).then((response) => {

                // Create a downloadable link for the Excel file
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${project}_biochar_lab_samples.xlsx`); // Set the file name
                document.body.appendChild(link);
                link.click();
                link.remove();
            })

        } catch (err) {
            console.error('Error downloading Biochar Lab Samples:', err);
        }
    };

    return (
        <>
            <h2>Biochar Lab Samples (C_ORG & H:C)</h2>
            <Button variant='contained' sx={{ height: 'auto', float: 'right', mt: 2, mb: 2 }} onClick={handleDownload}>Download Excel</Button>
            <TableContainer key={0} component={Paper} >
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell size="small" align="left">Sample</TableCell>
                            <TableCell size="small" align="left">Sample Date</TableCell>
                            <TableCell size="small" align="left">Sample type (IPS or RS)</TableCell>
                            <TableCell size="small" align="left">H:Corg - Test value</TableCell>
                            <TableCell size="small" align="left">Organic C Content (OCb) - Test value</TableCell>
                            <TableCell size="small" align="left">Upload Lab Report</TableCell>
                            <TableCell size="small" align="left">Comments</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {biocharLabSamplesState?.map((data, index) => {
                            const labReport = data.get('labReport');
                            const isString = typeof labReport === 'string' && labReport !== 'null';
                            return (
                                <TableRow key={index}>
                                    <TableCell size="small" align="left">
                                        {data.get('sampleNumber')}
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        <FormControl>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                {/* {console.log(data.get('sampleDate'), 'date')} */}
                                                <DatePicker
                                                    slotProps={{
                                                        textField: {
                                                            error: false
                                                        }
                                                    }}
                                                    name={'date'}
                                                    value={data.get('sampleDate') ? moment(data.get('sampleDate'), 'YYYY-MM-DD') : null}
                                                    onChange={(e) => handleDateSelection(e.toDate(), data.get('id'))}
                                                    disabled={data.get('disabled') ?? false}
                                                    format="MM/DD/YYYY"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        <Select name='sampleType' value={data.get('sampleType')} disabled={data.get('disabled')} fullWidth onChange={(e) => handleInputChange(e, data.get('rowId'), 'sampleType')}>
                                            {sampleTypes?.map(type => {
                                                return (
                                                    <MenuItem key={type.id} value={type.name} >
                                                        {type.name}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        <TextField
                                            name='hCorg'
                                            value={data.get('hCorg')}
                                            onChange={(e) => handleInputChange(e, data.get('rowId'), 'hCorg')}
                                            type="number"
                                            disabled={data.get('disabled') ?? false}
                                        />
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        <TextField
                                            name='organicCarbonContent'
                                            value={data.get('organicCarbonContent')}
                                            onChange={(e) => handleInputChange(e, data.get('rowId'), 'organicCarbonContent')}
                                            type="number"
                                            disabled={data.get('disabled') ?? false}
                                        />
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        {isString ? (
                                            <a href={labReport} target="_blank" rel="noopener noreferrer">
                                                Report
                                            </a>
                                        ) : (
                                            <TextField
                                                disabled={data.get('disabled' ?? false)}
                                                name={'labReport'}
                                                defaultValue={labReport && labReport.name ? labReport.name : ''}
                                                onChange={(e) => handleSourceDoc(e, data.get('rowId'))}
                                                type="file"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell size="small" align="left">
                                        <TextField
                                            name='comments'
                                            value={data.get('comments')}
                                            onChange={(e) => handleInputChange(e, data.get('rowId'), 'comments')}
                                            type="text"
                                            disabled={data.get('disabled') ?? false}
                                        />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {error ? <Alert severity='error' sx={{ my: 2 }}>Please fill out all fields before submitting</Alert> : null}
            <Box sx={{ position: 'relative', mb: 10 }}>
                <Button variant='contained' sx={{ height: 'auto', mt: 2 }} onClick={handleAddItem}>Add Entry</Button>
                {loading ? <CircularProgress sx={{ position: 'absolute', left: '125px', top: '12px' }}></CircularProgress> : <Button variant='contained' sx={{ height: 'auto', mt: 2, ml: 3 }} onClick={handleSubmitData}>Submit Data</Button>}
                {loading && <Alert severity='info' sx={{ my: 2 }}>Please be patient during data submission as file uploads can take some time to complete</Alert>}
            </Box>
        </>
    )
}

export default BiocharLabSamples